import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { FormControl } from '@angular/forms';
import { CdcJsService } from '@spartacus/cdc/root';
import { BaseSite, BaseSiteService, LanguageService, OccEndpointsService, TranslationService } from '@spartacus/core';
import { combineLatest, Observable, Subject } from 'rxjs';
import { filter, map, switchMap, take } from 'rxjs/operators';
import { CDCConstants } from '../../../../src/app/constant/gigyaConstant';
import { GgCustomFormValidators } from '../Validators/gg-custom-form-validators';
import { CDCConsent, CdcSubscription } from '../model/cdcConsent.model';
import { EnvironmentService } from './environment.service';

declare let gigya: any;

@Injectable({
  providedIn: 'root',
})
export class CommonCdcEnabelService {
  cdcEnabled$: Observable<boolean>;
  migrationInfoEnabled$: Observable<boolean>;
  apiKey$: Observable<string>;
  mfaKey$: Observable<string>;
  subscriptionData$: Observable<CdcSubscription[]>;
  consents$: Observable<CDCConsent[]>;
  allConsents$: Observable<CDCConsent[]>;

  constructor(
    protected baseSiteService: BaseSiteService,
    protected cdcJsService: CdcJsService,
    protected languageService: LanguageService,
    protected translation: TranslationService,
    private http: HttpClient,
    private occEndpoints: OccEndpointsService,
    private environmentService: EnvironmentService
  ) {
    this.cdcEnabled$ = this.baseSiteService.getActive().pipe(
      switchMap((siteId) => this.baseSiteService.get(siteId)),
      map((baseSite: BaseSite | undefined) => baseSite?.cdcEnabled ?? false)
    );

    this.migrationInfoEnabled$ = this.baseSiteService.getActive().pipe(
      switchMap((siteId) => this.baseSiteService.get(siteId)),
      map((baseSite: BaseSite | undefined) => baseSite?.migrationInfoEnabled ?? false)
    );

    this.apiKey$ = this.baseSiteService.getActive().pipe(
      switchMap((siteId) => this.baseSiteService.get(siteId)),
      map((baseSite: BaseSite | undefined) => baseSite?.gigyaConfig?.gigyaApiKey ?? '')
    );

    this.mfaKey$ = this.baseSiteService.getActive().pipe(
      switchMap((siteId) => this.baseSiteService.get(siteId)),
      map((baseSite: BaseSite | undefined) => baseSite?.gigyaConfig?.mfa_key ?? CDCConstants.rbaPolicy)
    );

    this.subscriptionData$ = this.baseSiteService.getActive().pipe(
      switchMap((siteId) => this.baseSiteService.get(siteId)),
      map((baseSite: BaseSite | undefined) => baseSite?.gigyaConfig?.subscriptions ?? [])
    );

    this.allConsents$ = this.languageService.getActive().pipe(
      switchMap(() => {
        const url = this.occEndpoints.buildUrl('getCdcConsent');
        return this.http.get<CDCConsent[]>(url);
      })
    );

    this.consents$ = combineLatest([this.subscriptionData$, this.allConsents$]).pipe(
      map(([subscriptions, consent]) => {
        return consent.filter((val) =>
          subscriptions.some((subscription) => subscription.subscriptionConsentID === val.consentId)
        );
      })
    );
  }

  renderScreenSet(params) {
    //this.cdcJsService.loadCdcJavascript();
    this.cdcJsService
      .didLoad()
      .pipe(take(1))
      .subscribe(() => {
        gigya.accounts.showScreenSet(params);
      });
  }

  getAccountInfo(param) {
    this.cdcJsService
      .didLoad()
      .pipe(take(1))
      .subscribe(() => {
        gigya.accounts.getAccountInfo(param);
      });
  }

  setAccountInfo(param) {
    this.cdcJsService
      .didLoad()
      .pipe(take(1))
      .subscribe(() => {
        gigya.accounts.setAccountInfo(param);
      });
  }

  createAddress(event) {
    return {
      city: event.formModel.data.addresses[0].city,
      country: event.formModel.profile.country,
      houseNumber: event.formModel.data.addresses[0].houseNumber,
      id: Date.now(),
      isbilling: true,
      isdefault: true,
      isProfileAddress: true,
      isshipping: true,
      isVisibleInAddressBook: true,
      name: event.formModel.profile.firstName,
      phoneNumber: event.formModel.profile.phones.number,
      postalCode: event.formModel.data.addresses[0].postalCode,
      street: event.formModel.data.addresses[0].street,
      surName: event.formModel.profile.lastName,
      title: event.formModel.data.title,
    };
  }

  isEmpty(value) {
    return value == undefined || value == '';
  }

  validatePhone(selector): Observable<boolean> {
    const phoneNumber = document.getElementById(selector) as HTMLInputElement;

    return this.baseSiteService
      .getActive()
      .pipe(
        map(
          (baseSiteId) =>
            !(
              this.isControlVisible(selector) &&
              phoneNumber.value !== '' &&
              GgCustomFormValidators.phoneNumberValidator(
                new FormControl(phoneNumber.value),
                baseSiteId,
                this.environmentService.getCurrentEnvironment()
              )
            )
        )
      );
  }

  validatePostalCode(selector): Observable<boolean> {
    const postalCode = document.getElementById(selector) as HTMLInputElement;

    return this.baseSiteService
      .getActive()
      .pipe(
        map(
          (baseSiteId) =>
            !(
              this.isControlVisible(selector) &&
              postalCode.value !== '' &&
              GgCustomFormValidators.postalCodeValidator(
                new FormControl(postalCode.value),
                baseSiteId,
                this.environmentService.getCurrentEnvironment()
              )
            )
        )
      );
  }

  validateField(
    validationMethod: (selector: string) => Observable<boolean>,
    selector: string,
    errorTranslationKey: string,
    errorFieldConstant: string
  ): Promise<Record<string, string>> {
    return new Promise<Record<string, string>>((resolve) => {
      validationMethod(selector)
        .pipe(take(1))
        .subscribe((isValid) => {
          if (!isValid) {
            this.translation
              .translate(errorTranslationKey)
              .pipe(take(1))
              .subscribe((translatedError) => {
                resolve({ [errorFieldConstant]: translatedError });
              });
          } else {
            resolve({});
          }
        });
    });
  }

  isControlVisible(controlId: string): boolean {
    const control = document.getElementById(controlId);
    return control?.offsetParent !== null;
  }

  setLiteEmailAccount(email) {
    const url = this.occEndpoints.buildUrl('getLiteAccountCdc', { urlParams: { email } });
    return this.http.get(url);
  }

  handleLiteSubscriptionConsent(event: any, callback): void {
    // filter by empty consentGroup (other type consent on cdc)
    this.allConsents$
      .pipe(
        take(1),
        filter((consents) => Array.isArray(consents) && consents.length > 0),
        map((consents) => consents.filter((val) => val.consentGroup === ''))
      )
      .subscribe((consents) => {
        this.setLiteEmailAccount(event.formModel.email)
          .pipe(take(1))
          .subscribe((subscriptionData: any) => {
            if (!this.isEmpty(subscriptionData) && !this.isEmpty(subscriptionData.consents) && consents?.length > 0) {
              const consentsData = subscriptionData.consents.filter((val) =>
                consents.some((consent) => consent.consentId === val.consentId)
              );
              if (consentsData?.length > 0) {
                consentsData.forEach((consentData) => {
                  if (
                    this.isFormModelExist(event) &&
                    !this.isEmpty(event.formModel.preferences) &&
                    !this.isEmpty(event.formModel.preferences[consentData.consentId])
                  ) {
                    const userPreferences = event.formModel.preferences[consentData.consentId];
                    userPreferences.isConsentGranted = consentData.isConsentGranted;
                    event.formModel.displayedPreferences[consentData.consentId].docVersion =
                      consentData.currentDocVersion;
                  }
                });
              }
            }
            callback();
          });
      });
  }

  isFormModelExist(event: any): boolean {
    return !this.isEmpty(event) && !this.isEmpty(event.formModel);
  }
}
